import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';

export enum AccountFeatureFlag {
  Whatsapp = 'whatsapp',
  StoreAnonEvents = 'store_anon_events',
  JavascriptWebhook = 'javascript_webhook',
  LegacyCustomSmtpBilling = 'legacy_custom_smtp_billing',
  SubscriptionCenterM1 = 'subscription_center_m1',
  MultiLanguageSettings = 'multi_language_settings',
  ExitOnlyAfterMessage = 'exit_only_after_message',
  CdpEnabled = 'cdp_enabled',
  ParcelAmp = 'parcel_amp',
  CdpDataCatalogEnabled = 'cdp_data_catalog_enabled',
  VirtualSupportChatEnabled = 'virtual_support_chat_enabled',
  ParcelInHydra = 'parcel_in_hydra',
  InAppLegacyEditor = 'in_app_legacy_editor',
  TourtipAdventuresV1 = 'tourtip_adventures_v1',
  FlagCDPUIConsolidation = 'cdp_ui_consolidation',
  AiEnabled = 'ai_enabled',

  DarkModeToggle = 'dark-mode-toggle',
  LayoutRefresh2026 = 'layout-refresh-2026',
  GoalsTracking = 'goals-tracking',
  GoalsAiRecommendations = 'goals-ai-recommendations',
  McpExternalAccountToggle = 'mcp-external-account-toggle',
  UniversalSearchDialog = 'universal-search-dialog',
  TasksPerformanceDashboard = 'tasks_performance_dashboard_enabled',
  WorkspaceHealthDashboard = 'workspace_health_dashboard',
  WorkspaceHealthAiOptimize = 'workspace_health_ai_optimize',
  // Pulled from Ember feature flags
  ActivityLogsTimeRange = 'activity-logs-time-range',
  AiAutoSegmentNames = 'ai_auto_segment_names',
  DeliverabilityAnalytics = 'deliverability-analytics',
  TransactionalSms = 'transactional-sms',
  LLMSendTimeOptimization = 'llm-send-time-optimization',
  LLMCampaignSendTimeOptimization = 'llm-campaign-send-time-optimization',
  CustomRoles = 'custom-roles',
  NewsletterRateLimitWithTimezone = 'newsletter-rate-limit-timezone',
  InAppEditorLeadForm = 'in-app-editor-lead-form',
  DefaultFromIdentity = 'default-from-identity',
  EmailAutoTls = 'email-auto-tls',
  ConfiguredWebhooks = 'configured_webhooks',
  LineChannel = 'line-channel',
  LLMAction = 'llm-action',
  LLMActionBYOK = 'llm-action-byok',
  InAppForms = 'in-app-forms',
  TeamMembersExport = 'team_member_export',
  GlobalExitConditions = 'global-exit-conditions',
  ObjectWaitUntil = 'obj_wait_until',
  JourneyAttributes = 'journey-attributes',
  WhatsappNativeFreeText = 'whatsapp-native-free-text',
  ShowTemplatesSnippetsLayouts = 'show_templates_snippets_layouts',
  InAppGlobalStyles = 'in-app_global-styles',
  HalloweenStickies2025 = 'halloween_stickies_2025',
  OptimizedMessageLibrary = 'optimized-message-library',

  BuilderUxImprovements = 'builder-ux-improvements',
  DsSocialIcons = 'ds-social-icons',
  DsCountdown = 'ds-countdown',
  DsVideo = 'ds-video',
  WhatsAppDSEditor = 'whatsapp-ds-editor',
  DsUiPlayground = 'ds-ui-playground',
  AiAssistantV2 = 'ai_assistant_v2',
  InAppTooltips = 'in-app-tooltips',
  InAppLivePreview = 'in-app_live-preview',
  SubscriptionCenterChannels = 'subscription-center-channels',
  DsInfiniteCanvas = 'ds-infinite-canvas',
  DsUseTiptap = 'ds-use-tiptap',
  PausePlanM2 = 'pause-plan-m2',
  PavedRoad = 'paved_road',

  MultipleShortlinkDomains = 'multiple_shortlink_domains',
  NotificationsUx = 'notifications-ux',
  HomeUx = 'home-ux',
  MultiAccountSwitcher = 'multi-account-switcher',
}

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  subscription_center_m1: false, // enables milestone 0 and milestone 1 features of the subscription center
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  cdp_enabled: false, // disables cdp
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  parcel_in_hydra: false, // enables Parcel in Hydra
  in_app_legacy_editor: false, // enables the legacy in-app editor
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed
  tourtip_adventures_v1: true,
  ai_enabled: false, // enables the AI features in the app

  'dark-mode-toggle': false, // whether to show a dark mode toggle in the UI
  'layout-refresh-2026': false, // enables the 2026 layout refresh
  'goals-tracking': false, // enables the goals tracking feature
  'goals-ai-recommendations': false, // enables AI-powered goal recommendations on the goals page
  'mcp-external-account-toggle': false, // mcp external account toggle
  'universal-search-dialog': false, // enables the universal search dialog
  tasks_performance_dashboard_enabled: false, // enables the tasks performance dashboard
  workspace_health_dashboard: false, // enables the workspace health dashboard page
  workspace_health_ai_optimize: false, // enables AI-assisted optimization in workspace health
  // Ember feature flags
  'activity-logs-time-range': false, // enables the activity logs time range feature
  ai_auto_segment_names: false, // enables the AI auto segment names feature
  'deliverability-analytics': false, // enables the deliverability analytics feature
  'transactional-sms': false, // enables the transactional SMS feature
  'llm-send-time-optimization': false, // enables the LLM send time optimization feature
  'llm-campaign-send-time-optimization': false, // enables the LLM campaign send time optimization feature
  'custom-roles': false, // enables the custom roles feature
  'newsletter-rate-limit-timezone': false, // enables the newsletter rate limit timezone feature
  'in-app-editor-lead-form': false, // enables the in-app editor lead form feature
  'default-from-identity': false, // enables the default from identity feature
  'email-auto-tls': false, // enables the email auto TLS feature
  configured_webhooks: false, // enables the configured webhooks feature
  'line-channel': false, // enables the line channel feature
  'llm-action': false, // enables the LLM action feature
  'llm-action-byok': false, // enables the LLM action BYOK feature
  'in-app-forms': false, // enables the in-app forms feature
  team_member_export: false, // enables the team member export feature
  'global-exit-conditions': false, // enables the global exit conditions feature
  obj_wait_until: false, // enables the obj wait until feature
  'journey-attributes': false, // enables the journey attributes feature
  'whatsapp-native-free-text': false, // enables the WhatsApp native free text feature
  show_templates_snippets_layouts: false, // enables the show templates snippets layouts feature
  'in-app_global-styles': false, // enables the in-app global styles feature
  halloween_stickies_2025: false, // enables the Halloween stickies 2025 feature
  'optimized-message-library': false, // enables the optimized message library feature
  'builder-ux-improvements': false, // enables the builder ux improvements feature
  'ds-social-icons': false, // enables the social icons component feature
  'ds-countdown': false, // enables the countdown component feature
  'ds-video': false, // enables the video component feature
  'whatsapp-ds-editor': false, // enables the WhatsApp design studio editor
  'ds-ui-playground': false, // enables the design studio UI playground
  ai_assistant_v2: false, // enables the AI assistant v2 feature
  'in-app-tooltips': false, // enables the in-app tooltips feature
  'in-app_live-preview': false, // enables the in-app live preview feature
  'subscription-center-channels': false, // enables the subscription center channels feature
  'ds-infinite-canvas': false, // enables the infinite canvas visual editor in Design Studio
  'ds-use-tiptap': false, // enables the tiptap-based visual editor in Design Studio
  'pause-plan-m2': false, // enables the real implementation of account pausing
  paved_road: false, // enables the CDP paved road onboarding experience
  multiple_shortlink_domains: false, // enables the multiple shortlink domains feature
  'notifications-ux': false, // enables the new notifications drawer UX
  'home-ux': false, // enables the home UX feature
  'multi-account-switcher': false, // enables the multi-account switcher
});

export const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

export const hasFeatureFlag = (flag: FeatureFlag) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag] ?? false;

  const account = authStore.get()?.account;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};
